import type { AccountCampaign } from '../../types/accountCampaign';
import type { Campaign } from '../../types/campaign';

// Granular Refresh spec §1. The bulk "Refresh from Campaign" action is
// only valid when every selected row is a Campaign-type row AND they all
// share the SAME Campaign — refreshing a mixed selection would silently
// merge incompatible content (§1.2).
//
// Pure: drives the disabled state + tooltip on the master grid's
// BulkActionBar and the Meta/Google grids' GridBulkExecutionBar. Mirrors
// the existing single-Campaign lead-form gate's shape (a `{ target,
// reason }` pair) so the call sites read consistently, but kept as its
// own helper rather than refactoring the lead-form one (different
// eligibility: lead form per-row-rejects opt-out rows, refresh disables
// the whole action).
//
// Rules (spec §1.1):
//   - all rows on Campaign A          → enabled  (target = A, reason null)
//   - mix of Campaign A + Campaign B  → disabled ("same Campaign")
//   - mix of Campaign A + no-Campaign → disabled ("same Campaign")
//   - all rows have no Campaign       → disabled ("no Campaign")
//   - shared Campaign not loadable    → disabled (defensive)

export type BulkRefreshGate = {
  // The single shared Campaign when the selection is valid; otherwise null.
  target: Campaign | null;
  // Tooltip text for the disabled button; null when enabled.
  reason: string | null;
};

const SAME_CAMPAIGN = 'All selected rows must use the same Campaign.';

export function resolveBulkRefreshTarget(
  selectedIds: string[],
  rows: AccountCampaign[],
  campaigns: Campaign[],
): BulkRefreshGate {
  const selectedSet = new Set(selectedIds);
  const selected = rows.filter((r) => selectedSet.has(r.id));

  if (selected.length === 0) {
    return { target: null, reason: 'Select one or more rows.' };
  }

  // A row is eligible only if it's a Campaign-type row with a campaignId.
  // Opt-out / unassigned rows have no Campaign content to refresh.
  const eligible = selected.filter(
    (r) => r.rowType === 'campaign' && r.campaignId !== null,
  );

  if (eligible.length === 0) {
    return {
      target: null,
      reason: 'No selected rows have an assigned Campaign.',
    };
  }

  // Any ineligible row in the mix → disabled (spec §1.1: "mix of Campaign
  // A + a row with no Campaign assigned → button disabled").
  if (eligible.length !== selected.length) {
    return { target: null, reason: SAME_CAMPAIGN };
  }

  const campaignIds = new Set(eligible.map((r) => r.campaignId as string));
  if (campaignIds.size > 1) {
    return { target: null, reason: SAME_CAMPAIGN };
  }

  const [campaignId] = [...campaignIds];
  const campaign = campaigns.find((c) => c.id === campaignId) ?? null;
  if (!campaign) {
    return {
      target: null,
      reason: "Selected rows' Campaign is not available.",
    };
  }

  return { target: campaign, reason: null };
}
